/**
 * Cognitive State Analyzer - Platform-agnostic
 * Detects engagement state from 468 MediaPipe landmarks + blendshapes
 * Ported from AlphaBuzz_2 mobile implementation, stripped of RN dependencies
 *
 * States: engaged | unengaged | focus | out_of_frame
 */

import type { CognitiveState } from './types';

interface Point { x: number; y: number; }

export interface CognitiveBaseline {
  gazeDeviation: number;
  eyeOpenness: number;
}

export interface HeadVector {
  yaw: number;
  pitch: number;
}

export interface ScreenAnchor {
  x: number;
  y: number;
}

// Roll compensation for camera rotation (critical on mobile)
const rotatePoint = (p: Point, center: Point, angle: number): Point => {
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
};

const avgPoint = (points: Point[]): Point => {
  const s = points.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
  return { x: s.x / points.length, y: s.y / points.length };
};

const compensateRoll = (lm: Point[]): Point[] => {
  const angle = Math.atan2(lm[263].y - lm[33].y, lm[263].x - lm[33].x);
  const center = avgPoint([lm[10], lm[152], lm[234], lm[454]]);
  return lm.map(p => rotatePoint(p, center, angle));
};

const computeGaze = (lm: Point[]): { horizontal: number; vertical: number } => {
  const leftEyeCenter = avgPoint([lm[33], lm[133], lm[159], lm[145]]);
  const rightEyeCenter = avgPoint([lm[263], lm[362], lm[386], lm[374]]);
  const leftIris = lm[468] ?? avgPoint([lm[159], lm[145], lm[33], lm[133]]);
  const rightIris = lm[473] ?? avgPoint([lm[386], lm[374], lm[263], lm[362]]);
  const faceWidth = Math.abs(lm[454].x - lm[234].x) || 1;

  return {
    horizontal: ((leftIris.x - leftEyeCenter.x) + (rightIris.x - rightEyeCenter.x)) / 2 / faceWidth,
    vertical: ((leftIris.y - leftEyeCenter.y) + (rightIris.y - rightEyeCenter.y)) / 2 / faceWidth,
  };
};

// Shared projection tuning (kept aligned with web session debug mapping).
const SCREEN_MAP = {
  xGain: 18,
  yGain: 28,
  yOffset: -0.02,
};

export const projectGazeToScreen = (
  adjustedGaze: { h: number; v: number },
  screenAnchor?: ScreenAnchor,
): ScreenAnchor => ({
  x: (screenAnchor?.x ?? 0.5) + (adjustedGaze.h * SCREEN_MAP.xGain),
  y: (screenAnchor?.y ?? (0.5 + SCREEN_MAP.yOffset)) + (adjustedGaze.v * SCREEN_MAP.yGain),
});

export const computeHeadVector = (landmarks: Point[]): HeadVector | null => {
  if (!landmarks || landmarks.length < 468) return null;
  const lm = compensateRoll(landmarks);

  const leftCheek = lm[234];
  const rightCheek = lm[454];
  const nose = lm[1] ?? lm[4];
  const forehead = lm[10];
  const chin = lm[152];
  if (!leftCheek || !rightCheek || !nose || !forehead || !chin) return null;

  const faceWidth = Math.max(1e-6, Math.abs(rightCheek.x - leftCheek.x));
  const faceHeight = Math.max(1e-6, Math.abs(chin.y - forehead.y));

  // Relative horizontal asymmetry around the nose (proxy for yaw).
  const leftDist = Math.abs(nose.x - leftCheek.x);
  const rightDist = Math.abs(rightCheek.x - nose.x);
  const yaw = (leftDist - rightDist) / faceWidth;

  // Relative nose vertical position between forehead and chin (proxy for pitch).
  const pitch = ((nose.y - forehead.y) / faceHeight) - 0.5;

  return { yaw, pitch };
};

// Expose a computeGazeVector helper (horizontal / vertical) for calibration
export const computeGazeVector = (landmarks: Point[]): { h: number; v: number } | null => {
  if (!landmarks || landmarks.length < 468) return null;
  const lm = compensateRoll(landmarks);
  const g = computeGaze(lm);
  return { h: g.horizontal, v: g.vertical };
};

export const computeEyeOpenness = (lm: Point[]): number => {
  const faceHeight = Math.abs(lm[10].y - lm[152].y) || 1;
  const leftOpen = Math.abs(lm[159].y - lm[145].y) / faceHeight;
  const rightOpen = Math.abs(lm[386].y - lm[374].y) / faceHeight;
  return (leftOpen + rightOpen) / 2;
};

export const computeGazeDeviation = (landmarks: Point[], calibrationOffset?: { h: number; v: number }): number | null => {
  if (!landmarks || landmarks.length < 468) return null;
  const lm = compensateRoll(landmarks);
  const gaze = computeGaze(lm);
  const adjH = gaze.horizontal - (calibrationOffset?.h ?? 0);
  const adjV = gaze.vertical - (calibrationOffset?.v ?? 0);
  return Math.sqrt(adjH ** 2 + adjV ** 2);
};

export const analyzeCognitiveState = (
  landmarks: Point[],
  _blendshapes?: Record<string, number>,
  headRotation?: { yaw: number; pitch: number; roll: number },
  calibrationOffset?: { h: number; v: number },
  headCalibrationOffset?: { yaw: number; pitch: number },
  screenCalibrationAnchor?: ScreenAnchor,
  deviceProfile?: { isMobileRuntime?: boolean },
): CognitiveState => {
  if (!landmarks || landmarks.length < 468) {
    return { state: 'out_of_frame', confidence: 0 };
  }

  // Mobile: iris landmarks are unreliable — use head rotation only
  if (deviceProfile?.isMobileRuntime) {
    if (headRotation && Math.abs(headRotation.yaw) > 0.45) {
      return { state: 'unengaged', confidence: 0.85 };
    }
    if (headRotation && Math.abs(headRotation.pitch) > 0.35) {
      return { state: 'unengaged', confidence: 0.80 };
    }
    return { state: 'engaged', confidence: 0.85 };
  }

  // Head rotated away → unengaged (calibration-relative when available)
  const headVec = computeHeadVector(landmarks);
  if (headVec && headCalibrationOffset) {
    const dYaw = headVec.yaw - headCalibrationOffset.yaw;
    const dPitch = headVec.pitch - headCalibrationOffset.pitch;
    if (Math.abs(dYaw) > 0.22 || Math.abs(dPitch) > 0.24) {
      return { state: 'unengaged', confidence: 0.9 };
    }
  } else if (headRotation && Math.abs(headRotation.yaw) > 0.65) {
    // Fallback absolute threshold when calibration reference is unavailable.
    return { state: 'unengaged', confidence: 0.9 };
  }

  // Eye gaze away from screen → unengaged.
  // We project adjusted gaze to normalized screen coordinates and use a strict in-screen test.
  const lm = compensateRoll(landmarks);
  const gaze = computeGaze(lm);
  // Apply calibration offset if present (reduces apparent deviation when user looks at screen)
  const adjH = gaze.horizontal - (calibrationOffset?.h ?? 0);
  const adjV = gaze.vertical - (calibrationOffset?.v ?? 0);
  // Use the same mapping used by the demo dot so cognitive state and visualization are coherent.
  const screenPoint = projectGazeToScreen({ h: adjH, v: adjV }, screenCalibrationAnchor);
  const screenX = screenPoint.x;
  const screenY = screenPoint.y;
  const tolerantMargin = 0.16; // allow edge margin for small camera/gaze noise (user dot may be nearly edge)
  const insideScreen = screenX >= -tolerantMargin && screenX <= 1 + tolerantMargin && screenY >= -tolerantMargin && screenY <= 1 + tolerantMargin;

  if (!insideScreen) {
    try { console.log(`[EIE][COGDBG] outside screen x=${screenX.toFixed(3)} y=${screenY.toFixed(3)} adj=${adjH.toFixed(4)},${adjV.toFixed(4)}`); } catch {}
    return { state: 'unengaged', confidence: 0.9 };
  }

  // Inside screen counts as engaged by design.
  return { state: 'engaged', confidence: 0.9 };
};
